import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Page } from "shared/ui";
import { useModal } from "shared/hooks";
import { FormikProvider, useFormik } from "formik";
import { Button } from "antd";
import { noop } from "shared/helpers";
import { initialTemplateConfigFormikValues } from "shared/constants";
import {
  addTemplateConfigSchema,
  editTemplateConfigSchema,
} from "shared/schemas";
import { useGetReportCategoriesQuery } from "shared/api";
import { EditTemplateConfigModal, TemplatesConfigTable } from "./ui";
import { AddTemplateConfigModal } from "./ui/AddTemplateConfigModal";

interface TemplatesConfigProps {}

const TemplatesConfig: FC<TemplatesConfigProps> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isOpenAddModal, showAddModal, hideAddModal] = useModal();
  const [isOpenEditModal, showEditModal, hideEditModal] = useModal();

  const { data: reportCategories = [] } = useGetReportCategoriesQuery({});

  const formattedReportCategoriesOptions = reportCategories?.map((report) => ({
    label: report.name,
    value: report.id,
  }));

  const templateConfigValidation = useMemo(() => {
    if (isOpenAddModal) {
      return addTemplateConfigSchema;
    }
    return editTemplateConfigSchema;
  }, [isOpenAddModal]);

  const templateConfigFormik = useFormik({
    initialValues: initialTemplateConfigFormikValues,
    validationSchema: templateConfigValidation,
    onSubmit: noop,
  });

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <FormikProvider value={templateConfigFormik}>
      <Page
        title={t("reports.title")}
        buttonTitle={t("reports.add-config")!}
        onClick={showAddModal}
      >
        <div className="flex gap-2 mb-2">
          <Button
            onClick={() => navigate("/recipient-list")}
            type="primary"
            style={{ backgroundColor: themeColor }}
          >
            Report Scheduling
          </Button>

          <Button
            onClick={() => navigate("/report-categories")}
            type="primary"
            style={{ backgroundColor: themeColor }}
          >
            Report Categories
          </Button>
        </div>

        <TemplatesConfigTable showEditModal={showEditModal} />

        <AddTemplateConfigModal
          isOpen={isOpenAddModal}
          onClose={hideAddModal}
          formattedReportCategoriesOptions={formattedReportCategoriesOptions}
        />

        <EditTemplateConfigModal
          isOpen={isOpenEditModal}
          onClose={hideEditModal}
          formattedReportCategoriesOptions={formattedReportCategoriesOptions}
        />
      </Page>
    </FormikProvider>
  );
};

export { TemplatesConfig };
